import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Badge, Button, Spinner, Alert, Table, Form } from 'react-bootstrap';
import axios from 'axios';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../config/api';
import AppIcon from './AppIcon';
import AddMutualFundToPortfolioModal from './AddMutualFundToPortfolioModal';

interface NavEntry {
  date: string;
  nav: number;
}

interface FundMeta {
  fund_house?: string;
  scheme_type?: string;
  scheme_category?: string;
  scheme_code?: number;
  scheme_name?: string;
}

interface MutualFundDetailData {
  id: number;
  stockid: number;
  title: string;
  body: string;
  isin: string;
  current_nav: number | null;
  nav_history: NavEntry[];
  meta: FundMeta;
}

type ChartRange = '1mo' | '3mo' | '6mo' | '1y' | '3y' | 'max';

const CHART_RANGES: Array<{ value: ChartRange; label: string; title: string }> = [
  { value: '1mo', label: '1M', title: '1 Month' },
  { value: '3mo', label: '3M', title: '3 Months' },
  { value: '6mo', label: '6M', title: '6 Months' },
  { value: '1y',  label: '1Y', title: '1 Year' },
  { value: '3y',  label: '3Y', title: '3 Years' },
  { value: 'max', label: 'Max', title: 'Max' },
];

const formatCurrency = (value: number | null) => {
  if (value === null || value === undefined) return 'N/A';
  return `₹${value.toFixed(4)}`;
};

const formatDate = (dateStr: string) => {
  const [d, m, y] = dateStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
};

const formatAxisTick = (dateStr: string) => {
  const [, m, y] = dateStr.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border rounded p-2 shadow-sm" style={{ fontSize: 13 }}>
        <div className="text-muted">{label}</div>
        <div className="fw-bold text-primary">NAV: ₹{payload[0].value.toFixed(4)}</div>
      </div>
    );
  }
  return null;
};

const MutualFundDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [fund, setFund] = useState<MutualFundDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [showAddToPortfolio, setShowAddToPortfolio] = useState(false);
  const [chartRange, setChartRange] = useState<ChartRange>('1y');

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        if (fund) {
          setChartLoading(true);
        } else {
          setLoading(true);
        }
        const response = await axios.get(`${API_BASE_URL}/mutual-funds/${id}/`, {
          params: { range: chartRange },
        });
        setFund(response.data);
      } catch (err: any) {
        setError('Failed to load fund details.');
      } finally {
        setLoading(false);
        setChartLoading(false);
      }
    };
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, API_BASE_URL, chartRange]);

  // Thin chart data to ≤120 points for performance
  const chartData = React.useMemo(() => {
    if (!fund?.nav_history) return [];
    const history = fund.nav_history;
    if (history.length <= 120) return history;
    const step = Math.floor(history.length / 120);
    return history.filter((_, i) => i % step === 0 || i === history.length - 1);
  }, [fund]);

  const navChange = chartData.length >= 2 ? chartData[chartData.length - 1].nav - chartData[0].nav : null;
  const navChangePct =
    navChange !== null && chartData[0]?.nav > 0
      ? (navChange / chartData[0].nav) * 100
      : null;

  const selectedRange = CHART_RANGES.find((r) => r.value === chartRange) ?? CHART_RANGES[3];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error && !fund) return <Alert variant="danger">{error}</Alert>;
  if (!fund) return null;

  return (
    <div className="instrument-detail-page instrument-detail-page--fund">
      {/* Back button */}
      <Button variant="outline-secondary" size="sm" className="instrument-back-button mb-3" onClick={() => navigate('/mutual-funds')}>
        <AppIcon name="arrow-left" /> Back to Mutual Funds
      </Button>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {addSuccess && <Alert variant="success" dismissible onClose={() => setAddSuccess('')}>{addSuccess}</Alert>}

      {/* Header card */}
      <Card className="instrument-hero-card mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-start">
            <Col md={8}>
              <h4 className="fw-bold mb-1">{fund.title}</h4>
              {fund.meta.scheme_category && (
                <Badge bg="secondary" className="me-2 mb-2">{fund.meta.scheme_category}</Badge>
              )}
              {fund.meta.scheme_type && (
                <Badge bg="info" text="dark" className="mb-2">{fund.meta.scheme_type}</Badge>
              )}
              {fund.meta.fund_house && (
                <div className="text-muted mt-1" style={{ fontSize: 14 }}>
                  <strong>Fund House:</strong> {fund.meta.fund_house}
                </div>
              )}
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <div className="text-muted" style={{ fontSize: 13 }}>Current NAV</div>
              <div className="fw-bold" style={{ fontSize: 28 }}>{formatCurrency(fund.current_nav)}</div>
              {navChangePct !== null && (
                <div className={navChangePct >= 0 ? 'text-success' : 'text-danger'} style={{ fontSize: 14 }}>
                  {navChangePct >= 0 ? '▲' : '▼'} {Math.abs(navChangePct).toFixed(2)}% ({selectedRange.label})
                </div>
              )}
              <Button
                variant="primary"
                size="sm"
                className="mt-2"
                onClick={() => setShowAddToPortfolio(true)}
              >
                + Add to Portfolio
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <AddMutualFundToPortfolioModal
        show={showAddToPortfolio}
        fund={fund}
        onHide={() => setShowAddToPortfolio(false)}
        onSuccess={(addedFund) => setAddSuccess(`${addedFund.title} was added to your portfolio.`)}
      />

      {/* Key details row */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="instrument-metric-card h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted mb-1" style={{ fontSize: 13 }}>AMFI Code</div>
              <div className="fw-bold fs-5">{fund.stockid}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="instrument-metric-card h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted mb-1" style={{ fontSize: 13 }}>ISIN</div>
              <div className="fw-bold fs-5"><code>{fund.isin || 'N/A'}</code></div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="instrument-metric-card h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted mb-1" style={{ fontSize: 13 }}>{selectedRange.label} NAV Change</div>
              {navChange !== null ? (
                <div className={`fw-bold fs-5 ${navChange >= 0 ? 'text-success' : 'text-danger'}`}>
                  {navChange >= 0 ? '+' : ''}₹{navChange.toFixed(4)}
                </div>
              ) : (
                <div className="text-muted">N/A</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* NAV History Chart */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h5 className="mb-0">NAV History</h5>
          <Form.Select
            size="sm"
            style={{ width: 'auto', minWidth: 130 }}
            value={chartRange}
            onChange={(e) => setChartRange(e.target.value as ChartRange)}
            disabled={chartLoading}
          >
            {CHART_RANGES.map((r) => (
              <option key={r.value} value={r.value}>{r.title}</option>
            ))}
          </Form.Select>
        </Card.Header>
        <Card.Body className="position-relative">
          {chartLoading && (
            <div className="position-absolute top-0 end-0 m-3 text-primary d-flex align-items-center gap-2" style={{ fontSize: 13, zIndex: 1 }}>
              <Spinner size="sm" animation="border" /> Updating
            </div>
          )}
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={300} style={{ opacity: chartLoading ? 0.55 : 1 }}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6c63ff" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6c63ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatAxisTick}
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `₹${v}`}
                  width={70}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="nav"
                  stroke="#6c63ff"
                  fill="url(#navGrad)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-muted py-4">No NAV history available for this fund.</div>
          )}
        </Card.Body>
      </Card>

      {/* About the Fund */}
      {fund.body && (
        <Card className="mb-4 shadow-sm">
          <Card.Header>
            <h5 className="mb-0">About the Fund</h5>
          </Card.Header>
          <Card.Body>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: '#444', marginBottom: 0 }}>{fund.body}</p>
          </Card.Body>
        </Card>
      )}

      {/* Recent NAV table */}
      {fund.nav_history.length > 0 && (
        <Card className="shadow-sm">
          <Card.Header>
            <h5 className="mb-0">Recent NAV (Last 10 Records)</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="text-end">NAV (₹)</th>
                  <th className="text-end">Change</th>
                </tr>
              </thead>
              <tbody>
                {[...fund.nav_history].reverse().slice(0, 10).map((entry, idx, arr) => {
                  const prev = arr[idx + 1];
                  const change = prev ? entry.nav - prev.nav : null;
                  return (
                    <tr key={entry.date}>
                      <td>{formatDate(entry.date)}</td>
                      <td className="text-end">₹{entry.nav.toFixed(4)}</td>
                      <td className={`text-end ${change === null ? '' : change >= 0 ? 'text-success' : 'text-danger'}`}>
                        {change === null ? '—' : `${change >= 0 ? '+' : ''}₹${change.toFixed(4)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default MutualFundDetail;
